import React, { Component } from 'react';
import PropTypes from 'prop-types';
import clone from 'clone';
import { withTranslation } from 'react-i18next';
import {
  Container,
  Button,
  Dimmer,
  Grid,
  Loader,
  Segment,
  Table,
} from 'semantic-ui-react';
import { fetchM } from '../../actions/fetchM';
import { flashSuccessMessage, flashErrorMessage } from '../../actions/flash';
import NavBar from '../../components/NavBar';
import BankerItem from '../../components/BankerItem';
import BankersList from '../modals/BankersList';

class List extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    token: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.object),
    fetch: PropTypes.func,
    onCheck: PropTypes.func,
    t: PropTypes.func.isRequired,
  }
  static getPropsToState = (props) => {
    const loading = props.loading;
    const count = props.count;
    const data = [].concat(props.data);
    return {
      data,
      oriData: clone(data),
      loading,
      count,
    };
  }
  constructor(props) {
    super(props);
    const state = List.getPropsToState(props);
    this.state = {
      ...state,
      currentFocus: null,
      edit: false,
      hasChanges: false,
    };
  }
  componentWillReceiveProps(nextProps) {
    const state = List.getPropsToState(nextProps);
    if (JSON.stringify(state.oriData) === JSON.stringify(this.state.oriData)) {
      state.data = this.state.data;
    }
    this.setState(state);
  }
  setComponentState(state = {}, callback) {
    return new Promise((res) => {
      this.setState(state, () => {
        if (callback) {
          callback();
        }
        res(state);
      });
    });
  }
  async updatePreferredBankers() {
    const { data } = this.state;
    const { store } = this.context;
    const { t } = this.props;
    try {
      await store.dispatch(fetchM('/bankers/preferred/', {
        credentials: 'include',
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': this.props.token,
        },
        body: JSON.stringify(data),
      }));
    } catch (err) {
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        return;
      }
      Bugsnag.notifyException(err);
      store.dispatch(flashErrorMessage({ message: t('There was an error with your request. Please try again.', { ns: 'translation' }) }));
      return;
    }
    if (this.props.fetch) {
      await this.props.fetch();
    }
    await this.setComponentState({ hasChanges: false }, () => {
      this.handleOnCheck(false);
    });
    store.dispatch(flashSuccessMessage({ header: t('Update Success', { ns: 'translation' }), message: t('Preferred bankers successfully updated.', { ns: 'translation' }) }));
  }
  handleEdit = () => {
    this.setState({ edit: !this.state.edit }, () => {
      if (!this.state.edit) {
        this.updatePreferredBankers();
      }
    });
  }
  handleCancelEdit = () => {
    this.setState({
      edit: false,
      data: clone(this.state.oriData),
    }, () => {
      this.handleOnCheck(false);
    });
  }
  handleOnSelect = (e, currentFocus) => {
    if (this.state.currentFocus !== currentFocus) {
      this.setState({ currentFocus });
    }
  }
  handleShowAddBanker = (e) => {
    e.preventDefault();
    this.setState({
      showAddBanker: true,
    });
  }
  handleCloseAddBanker = (e) => {
    if (e) { e.preventDefault(); }
    this.setState({
      showAddBanker: false,
    });
  }
  handleAddBanker = (e, data) => {
    e.preventDefault();
    this.setState({
      data,
      showAddBanker: false,
    }, () => {
      this.updatePreferredBankers();
    });
  }
  handleChangePosition = (index, banker, direction = 'up') => {
    if (this.state.edit) {
      const { data } = this.state;
      if (direction === 'up') {
        if (index > 0) {
          data.splice(index, 1);
          data.splice(index - 1, 0, banker);
        }
      } else if (direction === 'down') {
        if (index < data.length) {
          data.splice(index, 1);
          data.splice(index + 1, 0, banker);
        }
      }
      this.setState({ data }, () => {
        this.handleHasChange();
      });
    }
  }
  handleRemoveBanker = (index) => {
    if (this.state.edit) {
      const { data } = this.state;
      data.splice(index, 1);
      this.setState({ data }, () => {
        this.handleHasChange();
      });
    }
  }
  handleHasChange() {
    if (!this.state.hasChanges) {
      this.setState({ hasChanges: true }, () => {
        this.handleOnCheck(true);
      });
    }
  }
  handleOnCheck = (hasChanges = false, callback) => {
    if (this.props.onCheck) {
      this.props.onCheck(hasChanges, callback);
    }
  }
  render() {
    const { t } = this.props;
    return (
      <Container>
        <NavBar
          header={t('Preferred Bankers', { ns: 'translation' })}
          rightNav={
            <Button
              primary={!this.state.edit}
              positive={this.state.edit}
              content={this.state.edit ? t('Save', { ns: 'translation' }) : t('Edit', { ns: 'translation' })}
              onClick={this.handleEdit}
              disabled={this.state.edit && !this.state.hasChanges}
            />
          }
        />
        <BankersList
          open={this.state.showAddBanker}
          onClose={this.handleCloseAddBanker}
          onConfirm={this.handleAddBanker}
          limit={10}
          selected={this.state.data}
          only="banker"
        />
        <Grid verticalAlign="middle" columns={1}>
          <Grid.Column>
            <Dimmer
              inverted style={{ zIndex: 20 }}
              active={this.state.loading && this.state.data.length > 0}
            >
              <Loader inline="centered">{t('Loading', { ns: 'translation' })}</Loader>
            </Dimmer>
            {
              this.state.data.length > 0 ? (
                <Table unstackable>
                  <Table.Body>
                    {
                      this.state.data.map((banker, idx) => (
                        <Table.Row
                          key={idx}
                          positive={this.state.currentFocus === banker}
                        >
                          <Table.Cell>
                            <BankerItem
                              showSummary
                              banker={banker}
                              onClick={this.handleOnSelect}
                              disabled={!this.state.edit}
                              leftComponent={
                                <Grid columns="equal" centered verticalAlign="middle">
                                  <Grid.Column style={{ color: '#bbbbbb' }}>
                                    {idx + 1}
                                  </Grid.Column>
                                  <Grid.Column>
                                    <Button.Group
                                      className={this.state.edit ? null : 'hidden'}
                                      vertical size="mini"
                                    >
                                      <Button
                                        compact
                                        icon="caret up"
                                        onClick={() => this.handleChangePosition(idx, banker, 'up')}
                                        disabled={!this.state.edit || idx === 0}
                                      />
                                      <Button
                                        compact
                                        icon="caret down"
                                        onClick={() => this.handleChangePosition(idx, banker, 'down')}
                                        disabled={!this.state.edit ||
                                          idx >= this.state.data.length - 1}
                                      />
                                    </Button.Group>
                                  </Grid.Column>
                                </Grid>
                              }
                              rightComponent={
                                <div className={this.state.edit ? null : 'hidden'}>
                                  <Button
                                    compact
                                    negative
                                    size="mini"
                                    icon="remove"
                                    onClick={() => this.handleRemoveBanker(idx)}
                                    disabled={!this.state.edit}
                                  />
                                </div>
                              }
                            />
                          </Table.Cell>
                        </Table.Row>
                      ))
                    }
                  </Table.Body>
                </Table>
              ) : (
                <Segment textAlign="center">
                  {
                    this.state.loading ?
                      <Loader active inline="centered">{t('Loading', { ns: 'translation' })}</Loader>
                    : t('No preferred banker found.', { ns: 'translation' })
                  }
                </Segment>
              )
            }
          </Grid.Column>
          <Grid.Column textAlign="center">
            <Button
              negative={this.state.edit}
              content={this.state.edit ? t('Cancel', { ns: 'screens' }) : t('Add Banker', { ns: 'screens' })}
              onClick={this.state.edit ? this.handleCancelEdit : this.handleShowAddBanker}
            />
          </Grid.Column>
        </Grid>
      </Container>
    );
  }
}

export default withTranslation()(List);



// WEBPACK FOOTER //
// ./src/containers/PreferredBankers/List.jsx